import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { storageGet, storageRemove, storageSet } from "../lib/drive/storage";

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const AUTISTA_SESSIONS_KEY = "autista_sessions";
export const AUTISTA_ACTIVE_ID_KEY = "autista_active_id";

const AutistaAuthContext = createContext(null);

function loadSessionsFromStorage() {
  try {
    return JSON.parse(storageGet(AUTISTA_SESSIONS_KEY) || "[]");
  } catch {
    return [];
  }
}

export function AutistaAuthProvider({ children }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(() => loadSessionsFromStorage());
  const [activeId, setActiveId] = useState(() => storageGet(AUTISTA_ACTIVE_ID_KEY) || null);
  const [loading, setLoading] = useState(true);
  const sessionsRef = useRef(sessions);
  const activeIdRef = useRef(activeId);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const activeAutista = sessions.find((s) => s.id === activeId) || null;
  const token = activeAutista?.token || null;

  useEffect(() => {
    storageSet(AUTISTA_SESSIONS_KEY, JSON.stringify(sessions));
  }, [sessions]);

  useEffect(() => {
    if (activeId) storageSet(AUTISTA_ACTIVE_ID_KEY, activeId);
    else storageRemove(AUTISTA_ACTIVE_ID_KEY);
  }, [activeId]);

  useEffect(() => {
    const stored = loadSessionsFromStorage();
    const storedActive = storageGet(AUTISTA_ACTIVE_ID_KEY);
    if (!stored.length) {
      setLoading(false);
      return;
    }
    const target = stored.find((s) => s.id === storedActive) || stored[0];
    (async () => {
      try {
        await axios.get(`${API_URL}/api/autista/me`, {
          headers: { Authorization: `Bearer ${target.token}` },
        });
        setSessions(stored);
        setActiveId(target.id);
      } catch (err) {
        if (err.response?.status === 401 && target.device_token) {
          try {
            const { data } = await axios.post(`${API_URL}/api/autista/auth/device-login`, {
              device_token: target.device_token,
            });
            const updated = stored.map((s) =>
              s.id === target.id ? { ...s, token: data.token, ...data.autista } : s
            );
            setSessions(updated);
            setActiveId(target.id);
          } catch {
            setSessions(stored.filter((s) => s.id !== target.id));
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const url = err.config?.url || "";
        const isAutista =
          url.includes("/api/autista/") &&
          !url.includes("/auth/login") &&
          !url.includes("/auth/device-login");
        if (err.response?.status === 401 && isAutista && !err.config._autistaRetry) {
          const active = sessionsRef.current.find((s) => s.id === activeIdRef.current);
          if (active?.device_token) {
            err.config._autistaRetry = true;
            try {
              const { data } = await axios.post(`${API_URL}/api/autista/auth/device-login`, {
                device_token: active.device_token,
              });
              setSessions((prev) =>
                prev.map((s) => (s.id === active.id ? { ...s, token: data.token, ...data.autista } : s))
              );
              err.config.headers.Authorization = `Bearer ${data.token}`;
              return axios(err.config);
            } catch {
              /* fall through */
            }
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const loginWithPassword = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/autista/auth/login`, { email, password });
    const newSession = { ...data.autista, device_token: data.device_token, token: data.token };
    setSessions((prev) => [...prev.filter((s) => s.id !== newSession.id), newSession]);
    setActiveId(newSession.id);
    return newSession;
  }, []);

  const logoutActive = useCallback(async () => {
    const current = sessionsRef.current.find((s) => s.id === activeIdRef.current);
    if (current?.device_token) {
      try {
        await axios.post(`${API_URL}/api/autista/auth/device-logout`, { device_token: current.device_token });
      } catch {
        /* ignore */
      }
    }
    const remaining = sessionsRef.current.filter((s) => s.id !== activeIdRef.current);
    setSessions(remaining);
    setActiveId(remaining[0]?.id || null);
    navigate("/autista-login", { replace: true });
  }, [navigate]);

  return (
    <AutistaAuthContext.Provider
      value={{ sessions, activeAutista, token, loading, loginWithPassword, logoutActive }}
    >
      {children}
    </AutistaAuthContext.Provider>
  );
}

export function useAutistaAuth() {
  const ctx = useContext(AutistaAuthContext);
  if (!ctx) throw new Error("useAutistaAuth must be used within AutistaAuthProvider");
  return ctx;
}
